import { Controller } from "@hotwired/stimulus"
import intlTelInput from "intl-tel-input"
import es from "intl-tel-input/locale/es"

// Teléfono con selector de país (shared/_phone_input): el hidden con name viaja siempre en
// E.164 con "+"; los utils se cargan aparte y formatean por país. Ver _docs/frontend/phone_input.md.
export default class extends Controller {
  static targets = ["input", "hidden"]
  static values = { country: { type: String, default: "cl" }, utilsUrl: String }

  connect() {
    this.iti = intlTelInput(this.inputTarget, {
      initialCountry: this.countryValue,
      separateDialCode: true,
      countrySearch: true,
      countrySelectorMode: "AUTO",
      countryOrder: ["cl", "ar", "pe", "bo", "co", "br", "us"],
      countryNameLocale: "es",
      uiTranslations: es,
      loadUtils: this.hasUtilsUrlValue ? () => import(this.utilsUrlValue) : null,
      formatAsYouType: true,
      strictMode: true,
      placeholderNumberPolicy: "AGGRESSIVE",
      placeholderNumberType: "MOBILE",
      // Chile: el ejemplo de celular de libphonenumber es un fijo de Santiago; se muestra uno con 9.
      customPlaceholder: (example, country) => (country?.iso2 === "cl" ? "9 6123 4567" : example)
    })
    // Después de los listeners de la librería: el valor llega ya recortado y formateado (pegado).
    this.inputTarget.addEventListener("input", this.sync)
    this.inputTarget.addEventListener("countrychange", this.sync)
    this.inputTarget.addEventListener("open:countryselector", this.markPopup)
    document.addEventListener("turbo:before-cache", this.teardown)
    // El chip del país es hermano del input: toma su cuerpo de letra para leerse como uno.
    this.element.style.setProperty("--phone-input-font-size", getComputedStyle(this.inputTarget).fontSize)
    this.sync()
    // Con los utils ya cargados la librería reformatea el valor inicial: el hidden se rehace.
    this.iti.promise?.then(this.sync).catch(() => {})
  }

  disconnect() {
    this.teardown()
  }

  // input y countrychange: recompone el E.164 del hidden (getNumber con utils; si no, el "+"
  // escrito o país + dígitos) y avisa con phone-input:change (el wizard lo escucha).
  sync = () => {
    if (!this.iti) return

    const country = this.iti.getSelectedCountry()
    if (country?.iso2) this.countryValue = country.iso2 // el snapshot de Turbo restaura con esta bandera
    const raw = String(this.inputTarget.value || "").trim()
    const digits = raw.replace(/\D/g, "")
    let e164 = ""
    if (digits) {
      e164 = intlTelInput.utils ? this.iti.getNumber() : ""
      if (!e164) e164 = raw.startsWith("+") || !country?.dialCode ? `+${digits}` : `+${country.dialCode}${digits}`
    }

    this.hiddenTarget.value = e164
    this.dispatch("change", { detail: { e164 } })
  }

  // La lista a pantalla completa se monta en body: en Android nativo, arrastrar dentro
  // de ella no debe disparar el pull-to-refresh de la WebView.
  markPopup = () => {
    document.querySelector(".iti--fullscreen-popup")?.setAttribute("data-native-prevent-pull-to-refresh", "")
  }

  // También antes del snapshot de Turbo: si no, el widget queda duplicado al restaurar.
  teardown = () => {
    document.removeEventListener("turbo:before-cache", this.teardown)
    if (!this.iti) return

    this.inputTarget.removeEventListener("input", this.sync)
    this.inputTarget.removeEventListener("countrychange", this.sync)
    this.inputTarget.removeEventListener("open:countryselector", this.markPopup)
    this.iti.destroy()
    this.iti = null
  }
}
